import * as React from "react";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase";
import { reset } from "../../app/store/slice/counterSlice";
import LogoutIcon from "@mui/icons-material/Logout";
import CachedIcon from "@mui/icons-material/Cached";
import AccountBoxOutlinedIcon from "@mui/icons-material/AccountBoxOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import axios from "axios";

export default function AccountMenu() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  // Get user data from sessionStorage
  const userData = React.useMemo(() => {
    try {
      const data = sessionStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (error) {
      console.error("Error parsing userData:", error);
      return null;
    }
  }, []);

  const userName =
    userData?.user?.user_name ||
    userData?.dispatch_name ||
    userData?.shop_name ||
    "User";
  const roleName = userData?.role?.role_name || userData?.loginType || "User";
  const isSuperAdmin = userData?.isSuperAdmin || false;

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // ✅ Updated handleLogout to properly clear both storages
  const handleLogout = async () => {
    try {
      // Try Firebase signout
      try {
        await signOut(auth);
      } catch (firebaseError) {
        console.log("Firebase signout skipped or failed:", firebaseError);
      }

      // ✅ Get current tab ID and clear its session
      const tabId = sessionStorage.getItem("tabId");
      if (tabId) {
        const tabSessionKey = `session_${tabId}`;
        localStorage.removeItem(tabSessionKey);
        console.log(`🗑️ Cleared session for tab: ${tabId}`);
      }

      // ✅ Clear sessionStorage
      sessionStorage.clear();

      // ✅ Reset Redux state
      dispatch(reset());

      // ✅ Navigate to login
      navigate("/");

      toast.success("Logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      toast.error("Logout failed. Please try again.");
    }
  };

  const clearCache = async () => {
    try {
      const res = await axios.get("clear-cache");
      toast.success(res?.data?.message || "Cache cleared successfully");
    } catch (err) {
      console.error("Error clearing cache:", err);
      toast.error("Failed to clear cache");
    }
  };

  return (
    <React.Fragment>
      <Box sx={{ display: "flex", alignItems: "center", textAlign: "center" }}>
        <Tooltip title="Account settings">
          <IconButton
            onClick={handleClick}
            size="small"
            sx={{ ml: 2 }}
            aria-controls={open ? "account-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={open ? "true" : undefined}
          >
            <Avatar sx={{ width: 30, height: 30, }}>
              {userName.charAt(0).toUpperCase()}
            </Avatar>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            minWidth: 200,
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1,
            "& .MuiAvatar-root": {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 2,
            },
            "&::before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* User Profile Info */}
        <MenuItem
          sx={{ textTransform: "capitalize", cursor: "default" }}
          disableRipple
        >
          {isSuperAdmin ? (
            <AdminPanelSettingsOutlinedIcon sx={{ mr: 2, color: "#E63D24" }} />
          ) : (
            <AccountBoxOutlinedIcon sx={{ mr: 2 }} />
          )}
          <Box>
            <Box sx={{ fontWeight: 600 }}>{userName}</Box>
            <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
              {roleName}
            </Box>
          </Box>
        </MenuItem>

        {/* Divider */}
        <Box sx={{ borderTop: 1, borderColor: "divider", my: 1 }} />

        {/* Clear Cache */}
        <MenuItem onClick={clearCache}>
          <CachedIcon sx={{ mr: 2 }} /> Clear Cache
        </MenuItem>

        {/* Logout */}
        <MenuItem onClick={handleLogout}>
          <LogoutIcon sx={{ mr: 2 }} /> Logout
        </MenuItem>
      </Menu>
    </React.Fragment>
  );
}
